import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { adminApi } from '@/api';
import {
  Badge,
  Button,
  CardActions,
  EmptyState,
  ErrorState,
  Icon,
  ListCard,
  LoadingState,
  Modal,
  PageHeader,
  Pagination,
  SegmentedTabs,
  Select,
  Textarea,
  useToast,
} from '@/components/ui';
import { REPORT_TARGET_LABELS, REPORT_TONES, humanizeStatus } from '@/features/admin/status';
import { errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';
import { formatDate } from '@/utils/format';

/**
 * Reported content.
 *
 * Two things about this screen are load-bearing rather than cosmetic:
 *
 * **Resolve and dismiss are separate buttons, not one "done".** Resolved means
 * the content broke the rules and was acted on; dismissed means it was looked
 * at and was fine. Collapsing them would be one fewer click and would throw
 * away the only number that says how much reporting is real.
 *
 * **Deciding a report does not touch the content.** The link on each row is the
 * whole of the connection: an admin goes to the listing and takes it down
 * there, which is audited on its own. The record then shows what was claimed
 * *and* what was actually done, instead of implying the two are the same.
 */

const TABS = [
  { key: 'OPEN', label: 'Open' },
  { key: 'RESOLVED', label: 'Resolved' },
  { key: 'DISMISSED', label: 'Dismissed' },
];

const PAGE_SIZE = 20;

// The API returns what was reported, not where to find it in this console --
// it does not know our routes and should not. `target_exists` says whether
// there is still anything to link to.
const TARGET_ROUTES = {
  INTERNSHIP: paths.admin.internshipDetail,
  COMPANY: paths.admin.companyDetail,
  STUDENT: paths.admin.studentDetail,
};

function targetLink(row) {
  if (!row.target_exists) return null;
  return TARGET_ROUTES[row.target_type]?.(row.target_id) ?? null;
}

export function AdminReportsPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [status, setStatus] = useState('OPEN');
  const [targetType, setTargetType] = useState('');
  const [page, setPage] = useState(1);
  // { report, decision } -- one dialog for both decisions, because the only
  // thing that differs is the word on the button and what it means.
  const [deciding, setDeciding] = useState(null);
  const [note, setNote] = useState('');

  const params = {
    page,
    page_size: PAGE_SIZE,
    statuses: status,
    ...(targetType ? { target_type: targetType } : {}),
  };

  const { data, isPending, isPlaceholderData, isError, error, refetch } = useQuery({
    queryKey: ['admin-reports', params],
    queryFn: () => adminApi.listReports(params),
    placeholderData: keepPreviousData,
  });

  const decide = useMutation({
    mutationFn: ({ id, decision, note: text }) =>
      decision === 'RESOLVED'
        ? adminApi.resolveReport(id, text || undefined)
        : adminApi.dismissReport(id, text || undefined),
    onSuccess: (_report, variables) => {
      toast.success(variables.decision === 'RESOLVED' ? 'Report resolved' : 'Report dismissed');
      void queryClient.invalidateQueries({ queryKey: ['admin-reports'] });
      void queryClient.invalidateQueries({ queryKey: ['admin-overview'] });
      close();
    },
    onError: (decideError) => toast.error(errorMessage(decideError, 'Could not decide the report')),
  });

  function close() {
    setDeciding(null);
    setNote('');
  }

  const activeCount = targetType !== '' ? 1 : 0;

  function resetFilters() {
    setTargetType('');
    setPage(1);
  }

  const rows = data?.data ?? [];
  const meta = data?.meta;
  const resolving = deciding?.decision === 'RESOLVED';

  return (
    // On a phone or tablet held upright the list card runs to the bottom of the
    // screen.
    <div className="flex flex-col portrait:flex-1">
      <PageHeader
        title="Reported content"
        description="A report is a claim, not a verdict — nothing came down because somebody reported it. Acting on the content is a separate decision, taken on the listing or the account."
      />

      {/* Loading and errors render inside the card, so a refetch never
          unmounts the tabs and filter above them. */}
      <ListCard
        fill
        className="flex-1"
        isRefreshing={isPlaceholderData}
        toolbarStart={
          <SegmentedTabs
            variant="plain"
            tabs={TABS}
            value={status}
            onChange={(key) => {
              setStatus(key);
              setPage(1);
            }}
            label="Report status"
          />
        }
        toolbarEnd={
          <>
            <Select
              label="Kind"
              hideLabel
              size="sm"
              wrapperClassName="w-full sm:w-44"
              value={targetType}
              onChange={(event) => {
                setTargetType(event.target.value);
                setPage(1);
              }}
            >
              <option value="">Everything</option>
              <option value="INTERNSHIP">Internships</option>
              <option value="COMPANY">Companies</option>
              <option value="STUDENT">Students</option>
            </Select>
            {activeCount > 0 && (
              <Button variant="ghost" size="sm" onClick={resetFilters}>
                <Icon name="reset" size={14} />
                Reset
              </Button>
            )}
          </>
        }
        state={
          isError ? (
            <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
          ) : isPending ? (
            <LoadingState label="Loading reports…" />
          ) : rows.length === 0 ? (
            activeCount > 0 ? (
              <EmptyState
                title="Nothing matches these filters."
                description="No reports of this kind in this state."
                action={
                  <Button variant="secondary" size="sm" onClick={resetFilters}>
                    Reset filters
                  </Button>
                }
              />
            ) : (
              <EmptyState
                title={status === 'OPEN' ? 'No reports yet' : 'Nothing here'}
                description={
                  status === 'OPEN'
                    ? 'No reports are waiting for a decision.'
                    : 'No reports in this state.'
                }
              />
            )
          ) : null
        }
        footer={
          meta && (
            <Pagination
              className=""
              page={meta.page}
              pageSize={PAGE_SIZE}
              totalPages={meta.total_pages}
              totalItems={meta.total_items}
              onPageChange={setPage}
            />
          )
        }
      >
        <ul className="divide-line divide-y">
          {rows.map((row) => (
            <li key={row.id} className="px-5 py-4 transition hover:bg-brand-50/40">
              <div className="flex flex-col items-start justify-between gap-4 sm:flex-row">
                <div className="min-w-0 flex-1">
                  <p className="text-ink flex flex-wrap items-center gap-2 font-medium">
                    {targetLink(row) ? (
                      <Link to={targetLink(row)} className="hover:underline">
                        {row.target_label}
                      </Link>
                    ) : (
                      <span>{row.target_label}</span>
                    )}
                    <Badge tone="neutral">
                      {REPORT_TARGET_LABELS[row.target_type] ?? humanizeStatus(row.target_type)}
                    </Badge>
                    <Badge tone={REPORT_TONES[row.status] ?? 'neutral'}>
                      {humanizeStatus(row.status)}
                    </Badge>
                    {/* One complaint is an opinion; several about one thing is
                          a pattern, and it is the only signal on this screen an
                          admin cannot get by reading the reason. */}
                    {row.target_report_count > 1 && (
                      <Badge tone="warning">{row.target_report_count} reports</Badge>
                    )}
                    {!row.target_exists && <Badge tone="neutral">Deleted since</Badge>}
                  </p>

                  <p className="text-ink mt-2 text-sm">{row.reason}</p>

                  <p className="text-ink-muted mt-2 text-xs">
                    Reported by {row.reporter_name ?? 'a deleted account'}
                    {row.reporter_email ? ` (${row.reporter_email})` : ''} ·{' '}
                    {formatDate(row.created_at)}
                  </p>

                  {row.status !== 'OPEN' && (
                    <p className="text-ink-muted mt-2 text-xs">
                      {humanizeStatus(row.status)} by {row.reviewed_by_name ?? 'an admin'} ·{' '}
                      {formatDate(row.reviewed_at)}
                      {row.resolution_note ? ` — ${row.resolution_note}` : ''}
                    </p>
                  )}
                </div>

                {row.status === 'OPEN' && (
                  // Dismiss is "nothing wrong here", not a sanction, so it
                  // stays neutral; Resolve is the decision and sits last.
                  <CardActions className="shrink-0 self-end sm:self-start">
                    <Button
                      variant="secondary"
                      size="sm"
                      title="Nothing wrong with it: close the report"
                      onClick={() => setDeciding({ report: row, decision: 'DISMISSED' })}
                    >
                      <Icon name="close" size={14} />
                      Dismiss
                    </Button>
                    <Button
                      variant="tintPositive"
                      size="sm"
                      title="It broke the rules and has been acted on"
                      onClick={() => setDeciding({ report: row, decision: 'RESOLVED' })}
                    >
                      <Icon name="check" size={14} />
                      Resolve
                    </Button>
                  </CardActions>
                )}
              </div>
            </li>
          ))}
        </ul>
      </ListCard>

      <Modal
        open={Boolean(deciding)}
        title={resolving ? 'Resolve this report' : 'Dismiss this report'}
        description={
          resolving
            ? 'The content broke the rules and has been acted on. This does not itself take anything down — do that on the listing or the account.'
            : 'The content was looked at and there is nothing wrong with it. Nothing changes for the person who was reported.'
        }
        onClose={close}
        footer={
          <>
            <Button variant="secondary" onClick={close} disabled={decide.isPending}>
              Cancel
            </Button>
            <Button
              variant={resolving ? 'primary' : 'secondary'}
              isLoading={decide.isPending}
              onClick={() =>
                decide.mutate({
                  id: deciding.report.id,
                  decision: deciding.decision,
                  note: note.trim(),
                })
              }
            >
              {resolving ? 'Resolve' : 'Dismiss'}
            </Button>
          </>
        }
      >
        <Textarea
          label="Note"
          rows={3}
          hint="Optional, and only ever shown to admins. It is what the next person reading this report has to go on."
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />
      </Modal>
    </div>
  );
}
